import type { MouseEvent } from "react";
import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Button } from "@volca/ui/components/ui/button";
import {
  Frame,
  FrameDescription,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@volca/ui/components/ui/frame";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import {
  AddedDate,
  AssigneeSelect,
  InvoiceCardTitleLink,
  ManualJobMatchButton,
  StaleDataNotice,
  StatusBadge,
  StFilingTargetLink,
} from "@/accounts-payable/common";
import type { ApActionType, ApReviewQueueItem } from "@/accounts-payable/types";
import {
  AP_TAB,
  getBulkApproveIds,
  invoiceStatusMeta,
  isBulkApprovable,
  isPendingReviewAction,
  needsJob,
  stPurchaseOrderUrl,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";
import { ActionRow } from "./action-row";
import { DismissAllButton } from "./dismiss-all-button";
import { SupersedeNotice } from "./supersede-notice";

const NO_IDS: ReadonlySet<string> = new Set();

export const InvoiceGroupCard = ({
  item,
  businessId,
  onResolve,
  enableBulkApprove,
  onApproveAll,
  bulkApprovingIds = NO_IDS,
  resolvingIds = NO_IDS,
  onOpenInvoice,
  actionTypes = [],
}: {
  item: ApReviewQueueItem;
  businessId: string | null;
  onResolve: (id: string, decision: "approved" | "rejected") => void;
  enableBulkApprove?: boolean;
  onApproveAll?: (actionIds: string[]) => void;
  bulkApprovingIds?: ReadonlySet<string>;
  resolvingIds?: ReadonlySet<string>;
  onOpenInvoice?: (event: MouseEvent<HTMLAnchorElement>) => void;
  actionTypes?: ApActionType[];
}) => {
  const pendingActions = item.last_completed_agent_run.actions.filter(
    isPendingReviewAction,
  );
  const reviewActions = actionTypes.length
    ? pendingActions.filter((a) => actionTypes.includes(a.action_type))
    : pendingActions;
  const hiddenCount = pendingActions.length - reviewActions.length;
  const awaitingJob = needsJob(item);
  // "Approve all" is meaningless on a group whose only rows are acknowledgements.
  const approvableCount = reviewActions.filter(isBulkApprovable).length;
  const showApproveAll =
    !awaitingJob && !!enableBulkApprove && approvableCount > 1;
  const showPill = awaitingJob || showApproveAll;
  const bulkApproving = reviewActions.some((a) => bulkApprovingIds.has(a.id));
  const showDismissAll = reviewActions.length > 1;
  const hiddenNote =
    hiddenCount === 1
      ? "1 more action on this invoice isn't shown by the filter."
      : `${hiddenCount} more actions on this invoice aren't shown by the filter.`;
  return (
    <Frame stacked dense data-review-invoice-id={item.id} className="w-full">
      <FrameHeader>
        {/* Invoice header. The frame already paints this band. The status badge
          closes the title line rather than opening it, so vendor names align
          down the queue. On a phone the header stacks: identity, then its
          button under it, as the rows below put theirs under the text. */}
        <Stack
          direction="column"
          justify="between"
          align="stretch"
          gap={2}
          className="w-full sm:flex-row sm:flex-wrap sm:items-center sm:gap-4"
        >
          <Stack align="start" gap={0} className="min-w-0 sm:flex-1">
            {/* Line one is identity: who, and what state the invoice is in. */}
            <Stack direction="row" gap={2} align="center" className="flex-wrap">
              <FrameTitle className="truncate">{item.vendor_name}</FrameTitle>
              <StatusBadge meta={invoiceStatusMeta(item)} />
            </Stack>
            {/* The amount is pinned to the end of this second line, a fixed gap
              past the metadata, so it tracks the address line specifically
              rather than the wider of the two header lines. */}
            <Stack direction="row" gap={4} align="center" className="mt-1.5">
              {/* Everything that identifies the paperwork sits on one line at one
                size, so the two links don't read as different kinds of thing.
                Spaced rather than dotted: a wrapped line would leave a dot
                dangling at its end. The queue sorts by created_at (arrival),
                which the Invoices table calls Added. */}
              <Stack
                direction="row"
                gap={0}
                align="center"
                className="max-w-152 min-w-0 flex-wrap gap-x-3 gap-y-0.5 text-sm text-subtle"
              >
                <InvoiceCardTitleLink
                  invoiceId={item.id}
                  vendorName={item.vendor_name}
                  invoiceNumber={item.invoice_number}
                  fromTab={AP_TAB.review}
                  onClick={onOpenInvoice}
                />
                <StFilingTargetLink
                  document={item}
                  documentType={item.document_type}
                />
                {item.st_po_external_id && (
                  <ServiceTitanIconLink
                    url={stPurchaseOrderUrl(item.st_po_external_id)}
                    label={`Open purchase order #${item.st_po_external_id} in ServiceTitan`}
                  >
                    PO #{item.st_po_external_id}
                  </ServiceTitanIconLink>
                )}
                <span>
                  Added <AddedDate value={item.created_at} />
                </span>
                {item.service_address && (
                  <span className="truncate">{item.service_address}</span>
                )}
              </Stack>
              {/* Rendered only when there is a total, so an invoice without one
                shows no orphaned divider. A "-" implied a value of zero. */}
              {item.total_amount_cents ? (
                <Stack
                  direction="row"
                  gap={4}
                  align="center"
                  className="flex-none"
                >
                  <span className="h-5 w-px bg-line" aria-hidden />
                  <span className="text-sm font-semibold text-ink tabular-nums">
                    {formatCentsOrDashIfZero(item.total_amount_cents)}
                  </span>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
          {/* The owner, then a row's [primary pill][gap][✕] trailing structure,
            so "Approve all" / "Match to a job" land on the same edges as each
            row's Approve, and "Dismiss all" sits over the rows' ✕ column.
            Both cells keep their width when empty, so the owner and amount
            sit at one x down the queue. */}
          {/* On a phone the cells only exist when they hold a button, so the
            owner starts the line. The owner pill is narrower there, so it,
            "Approve all" and "Dismiss all" share one line; on the narrowest
            screens the buttons wrap under it rather than run off the card. */}
          <Stack
            direction="row"
            gap={2}
            align="center"
            justify="start"
            className="flex-none flex-wrap sm:flex-nowrap sm:justify-end"
          >
            <AssigneeSelect document={item} businessId={businessId} compact />
            <Stack
              gap={4}
              direction="row"
              align="center"
              justify="end"
              className={cn("flex-none sm:flex sm:min-w-30", {
                hidden: !showPill,
              })}
            >
              {awaitingJob && (
                <ManualJobMatchButton
                  businessId={businessId}
                  documentId={item.id}
                  agentRunId={item.agent_run_id}
                  size="default"
                  className="h-9 min-w-30 sm:h-8"
                />
              )}
              {showApproveAll && (
                <Button
                  variant="secondary"
                  className="h-9 min-w-30 sm:h-8"
                  disabled={bulkApproving}
                  loading={bulkApproving}
                  onClick={() =>
                    onApproveAll?.(getBulkApproveIds(reviewActions))
                  }
                >
                  Approve all
                </Button>
              )}
            </Stack>
            {showDismissAll ? (
              <DismissAllButton
                businessId={businessId}
                actionIds={reviewActions.map((a) => a.id)}
                hiddenCount={hiddenCount}
                documentName={[item.vendor_name, item.invoice_number]
                  .filter(Boolean)
                  .join(" ")}
              />
            ) : (
              <span className="hidden size-8 flex-none sm:block" aria-hidden />
            )}
          </Stack>
        </Stack>
        <StaleDataNotice
          run={item.last_completed_agent_run}
          layout="row"
          className="mt-2"
          actions
        />
        {awaitingJob && <SupersedeNotice />}
      </FrameHeader>
      {reviewActions.map((a) => (
        <FramePanel
          key={a.id}
          fit
          className="transition-colors hover:bg-neutral-subtle-hovered"
        >
          <ActionRow
            action={a}
            runActions={item.last_completed_agent_run.actions}
            businessId={businessId}
            document={{
              id: item.id,
              type: item.last_completed_agent_run.document_type,
              number: item.invoice_number,
              agentRunId: item.agent_run_id,
              hasJob: !!(
                item.st_job_external_id || item.st_project_external_id
              ),
            }}
            showDivider={false}
            resolving={resolvingIds.has(a.id) || bulkApprovingIds.has(a.id)}
            onResolve={(decision) => onResolve(a.id, decision)}
          />
        </FramePanel>
      ))}
      {hiddenCount > 0 && (
        <FramePanel fit>
          <FrameDescription>{hiddenNote}</FrameDescription>
        </FramePanel>
      )}
    </Frame>
  );
};
